/* eslint-disable consistent-return */
/* eslint-disable no-nested-ternary */
import React, { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { Link, useHistory, useLocation } from 'react-router-dom'
import block from 'bem-cn'

import dropAndWinsPng from 'shared/img/slots/drop-and-wins-logo-black.png'
import { ReactComponent as FavoriteProviderSVG } from 'shared/img/slots/favorite-provider.svg'
import { ReactComponent as FavoritesSVG } from 'shared/img/slots/favorites-svg.svg'
import { ReactComponent as PlaySVG } from 'shared/img/slots/play.svg'

import { mediaSize } from 'shared/style/var'

import { exclusiveGames } from 'modules/SlotsRoot/data/tabs'
import { getPureGameId } from 'modules/SlotsRoot/providersData/GameCatalog/uniqGameId'
import { ISlotViewItem } from 'modules/SlotsRoot/types/interfaces/Slots/Slots'
import { newLabelProviders } from 'modules/SlotsRoot/utils/slots'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { actions as slotsActions } from 'features/slots'
import { selectFavouriteList, selectNewGames } from 'features/slots/selectors'

import LoadableImage from 'components/LoadableImage'
import { addNotify } from 'components/Notify'
import Button from 'components/UIKit/Button/Button'

import './SlotsIcon.scss'

export interface ISlotsIconProps {
  slot: ISlotViewItem
  Image?: ReactNode
  isLive?: boolean
  isPreview?: boolean
}

const b = block('slots-icon')

const SlotsIcon: React.FC<ISlotsIconProps> = ({ slot, Image, isLive, isPreview }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()

  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const locale = useSelector(selectLocaleDict, shallowEqual).slots
  const favoriteList = useSelector(selectFavouriteList, shallowEqual)
  const newGamesList = useSelector(selectNewGames, shallowEqual)

  const isMobileOrTablet = useMediaQuery({ maxWidth: mediaSize.tablet })

  // eslint-disable-next-line no-undef
  const timeoutId = useRef<NodeJS.Timeout>()

  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const isFavorite = useMemo(
    () =>
      slot.tab.indexOf('favorite') !== -1 ||
      favoriteList?.findIndex(t => t === `${slot.gameId}_${slot.provider}`) !== -1,
    [favoriteList, slot],
  )

  useEffect(
    () => () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
      }
    },
    [],
  )

  const onFavoriteClick = useCallback(
    e => {
      if (!isAuth) return

      e.preventDefault()
      e.stopPropagation()

      if (isFavorite) {
        dispatch(slotsActions.removeFavoriteSlot(slot))
      } else {
        dispatch(slotsActions.addFavoriteSlot(slot))
      }
    },
    [slot, isFavorite, isAuth, dispatch],
  )

  const onClick = useCallback(
    (e, isButton: boolean) => {
      if (!isMobileOrTablet && !isButton) {
        e.preventDefault()

        return
      }

      if (!isAuth) {
        e.preventDefault()
        addNotify(locale.pleaseAuth)

        return
      }

      dispatch(slotsActions.setBackUrl(`${history.location.pathname}${history.location.search}`))

      if (isButton) {
        history.push({
          pathname: `/slots/${slot.routeProvider}/${slot.gameId}`,
          state: { prevPath: `${location.pathname}${location.search}` },
        })
      }
    },
    [
      dispatch,
      history,
      isAuth,
      isMobileOrTablet,
      locale.pleaseAuth,
      location.pathname,
      location.search,
      slot.gameId,
      slot.routeProvider,
    ],
  )

  const isDropAndWinSlot = useMemo(() => slot.tab.includes('Drops & Wins'), [slot])

  const isNew = useMemo(
    () => newLabelProviders.includes(slot.provider) || newGamesList.includes(getPureGameId(slot.gameId)),
    [slot.gameId, slot.provider],
  )

  const isExclusive = useMemo(() => exclusiveGames.includes(getPureGameId(slot.gameId)), [slot])

  return (
    <Link
      className={b({ isLive, isExclusive, isPreview })}
      to={{
        pathname: `/slots/${slot.routeProvider}/${slot.gameId}`,
        state: { prevPath: `${location.pathname}${location.search}` },
      }}
      // target={isAuth ? '_blank' : '_self'}
      key={slot.gameId}
      onClick={e => onClick(e, false)}>
      <div className={b('img-wrapper')}>
        {isNew ? <div className={b('new-label')}>{locale.new}</div> : null}
        {isExclusive && (
          <div className={b('exclusive-wrapper')}>
            <div className={b('exclusive')}>{locale.exclusive}</div>
          </div>
        )}
        {Image || (
          <LoadableImage
            src={slot.imageUrl}
            className={b('img', { loading: isLoading, error: isError })}
            onError={() => {
              setIsError(true)
              setIsLoading(false)
            }}
            onLoad={() => setIsLoading(false)}
            animationType="appearance"
          />
        )}
        {isLoading && !Image && <div className={b('img-plug')}>{slot.gameName}</div>}

        <div className={b('overlay')}>
          <div className={b('overlay-content')}>
            <div className={b('bottom')}>
              <div className={b('bottom-left')}>
                <span className={b('name')}>{slot.gameName}</span>
                <div className={b('btn-wrapper')}>
                  <Button text={locale.play} callback={e => onClick(e, true)} />
                </div>
              </div>
              <div className={b('bottom-right')}>
                {isFavorite ? (
                  <FavoriteProviderSVG className={b('favorite')} onClick={onFavoriteClick} />
                ) : (
                  <FavoritesSVG className={b('favorite')} onClick={onFavoriteClick} />
                )}
              </div>
            </div>
          </div>
          <div className={b('overlay-bg')} />
        </div>
        {isDropAndWinSlot && (
          <div className={b('dropWins-wrapper')}>
            <img src={dropAndWinsPng} alt="Drops & Wins" className={b('dropWins-image')} />
          </div>
        )}
      </div>
    </Link>
  )
}

export default memo(SlotsIcon)
